// OpenPanel browser client.
//
// This module wraps `@openpanel/web` with a non-blocking safety contract:
//   - Production-only by default (skip dev / preview builds unless
//     VITE_ANALYTICS_ENABLED_IN_DEV=true is explicitly set)
//   - Read clientId from env (VITE_OPENPANEL_CLIENT_ID); missing key → no-op
//   - Self-hosted API URL via VITE_OPENPANEL_API_URL (defaults to
//     https://op-api.jungleai.com)
//   - Cost-controlled OpenPanel session replay sampling
//   - Never throws — analytics-reporting failure must never break the user flow
//
// Used by:
//   - src/App.tsx (initialization + window.openpanel exposure for Tranzmit)
//   - src/backend/utils/helper-methods.ts (trackEvent + identifyUserForAnalytics)
//   - src/contextsAndHooks/AuthContext.tsx (clear() on sign-out)
//
import { OpenPanel } from "@openpanel/web";
import { getClientBuildInfo } from "./client-build-info";
import { sanitizeOpenPanelPayload } from "./openpanel-event-properties";

/**
 * Subset of the OpenPanel SDK surface we use in jungle-web2.
 * Defined so the no-op shim and the real client share a type.
 */
interface JungleOpenPanelClient {
  track(eventName: string, properties?: Record<string, unknown>): void;
  identify(profileObject: {
    profileId: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
    properties?: Record<string, unknown>;
  }): void;
  setGlobalProperties(properties: Record<string, unknown>): void;
  /**
   * Records OpenPanel's built-in `screen_view` for `pageUrl` and makes it the `__path` of every later
   * event in this page-load. Automatic screen views stay off (see `trackScreenViews` below); the only
   * caller is the public marketing site, via `trackMarketingSiteScreenView`.
   */
  screenView(pageUrl: string): void;
  /** Clears the current profile + session state. Call on sign-out. */
  clear(): void;
  /** True when the underlying SDK is real (not a no-op). */
  readonly isOpenPanelEnabled: boolean;
  /**
   * The underlying `@openpanel/web` instance, or `null` when we're in no-op
   * mode (dev / missing config). Use this when a third-party consumer needs
   * the full SDK surface beyond `track / identify / setGlobalProperties /
   * clear` — for example, Tranzmit's churn-flow SDK reads `window.openpanel`
   * and expects the real class with all methods + properties. Internal code
   * should keep using the safe wrapper methods above (try/catch + no-op).
   */
  readonly rawOpenPanelSdkInstanceOrNull: OpenPanel | null;
}

const DEFAULT_OPENPANEL_API_URL = "https://op-api.jungleai.com";
const DEFAULT_OPENPANEL_SESSION_REPLAY_SAMPLE_RATE = 0.05;

/**
 * Module-level guard so `forceStartOpenPanelHighFidelitySessionReplay` is
 * idempotent within a single page-load. Two voice calls in the same tab
 * shouldn't try to start the recorder twice (the replay module would stop
 * the in-progress recording and start a fresh one — harmless but pointless).
 *
 * Page-load scoped only. Not persisted — every new page-load starts fresh
 * at the baseline sample rate and only flips to full fidelity if the user
 * starts a voice call in THIS page-load.
 */
let hasForceStartedOpenPanelHighFidelityReplayThisPageLoad = false;

/**
 * No-op implementation. Returned when:
 *   - We're in a non-production build and VITE_ANALYTICS_ENABLED_IN_DEV isn't set
 *   - VITE_OPENPANEL_CLIENT_ID is missing or empty
 */
const noOpenPanelClient: JungleOpenPanelClient = {
  track: () => {},
  identify: () => {},
  setGlobalProperties: () => {},
  screenView: () => {},
  clear: () => {},
  isOpenPanelEnabled: false,
  rawOpenPanelSdkInstanceOrNull: null,
};

let openPanelSingletonClient: JungleOpenPanelClient = noOpenPanelClient;
let hasInitializedOpenPanelClient = false;

/**
 * Initialize the OpenPanel browser SDK. Idempotent — safe to call multiple
 * times (subsequent calls are no-ops). Mirrors the contract of
 * `initializeSentryForJungleWebApp` in `sentry-init.ts`.
 *
 * Call this once during app bootstrap (from `App.tsx`).
 */
export function initializeOpenPanelForJungleWebApp(): void {
  if (hasInitializedOpenPanelClient) {
    return;
  }
  hasInitializedOpenPanelClient = true;

  const isProductionBuild = import.meta.env.PROD === true;
  const isAnalyticsForcedOnInDev = (import.meta.env.VITE_ANALYTICS_ENABLED_IN_DEV as string | undefined) === "true";
  const shouldInitializeOpenPanel = isProductionBuild || isAnalyticsForcedOnInDev;

  if (!shouldInitializeOpenPanel) {
    return; // openPanelSingletonClient stays as the no-op
  }

  const openPanelClientIdFromEnv = import.meta.env.VITE_OPENPANEL_CLIENT_ID as string | undefined;
  if (!openPanelClientIdFromEnv || openPanelClientIdFromEnv.length === 0) {
    // Production build with no client ID configured. Log a warning so it's
    // visible in DevTools but don't crash — analytics failure should never
    // break the user flow.
    console.warn("[openpanel-client] VITE_OPENPANEL_CLIENT_ID is not set — OpenPanel disabled in this build.");
    return;
  }

  const openPanelApiUrlFromEnv =
    (import.meta.env.VITE_OPENPANEL_API_URL as string | undefined) || DEFAULT_OPENPANEL_API_URL;

  try {
    const sessionReplaySampleRateFromEnv = import.meta.env.VITE_OPENPANEL_SESSION_REPLAY_SAMPLE_RATE as
      string | undefined;
    const normalizedSessionReplaySampleRateFromEnv = sessionReplaySampleRateFromEnv?.trim();
    const parsedSessionReplaySampleRate = normalizedSessionReplaySampleRateFromEnv
      ? Number(normalizedSessionReplaySampleRateFromEnv)
      : DEFAULT_OPENPANEL_SESSION_REPLAY_SAMPLE_RATE;
    const sessionReplaySampleRate =
      Number.isFinite(parsedSessionReplaySampleRate) &&
      parsedSessionReplaySampleRate >= 0 &&
      parsedSessionReplaySampleRate <= 1
        ? parsedSessionReplaySampleRate
        : DEFAULT_OPENPANEL_SESSION_REPLAY_SAMPLE_RATE;

    // If the SDK's constructor will auto-start the recorder at full fidelity,
    // mark the page-load guard so a later voice-call-triggered force-start
    // becomes a no-op. The SDK's auto-start branch is
    // `enabled && Math.random() < sampleRate`; at sample-rate 1 the random
    // check is always true, so we know recording will start.
    if (sessionReplaySampleRate >= 1) {
      hasForceStartedOpenPanelHighFidelityReplayThisPageLoad = true;
    }

    const realOpenPanelClient = new OpenPanel({
      clientId: openPanelClientIdFromEnv,
      apiUrl: openPanelApiUrlFromEnv,
      // Runs after the SDK merges global properties, so it protects both
      // Jungle wrapper calls and raw `window.openpanel.track(...)` consumers.
      filter: sanitizeOpenPanelPayload,

      // Autocapture stays disabled so Jungle's explicit event taxonomy remains
      // the only product-event source. Enable intentionally if the product
      // team adopts OpenPanel's automatic screen or link events.
      trackScreenViews: false,
      trackOutgoingLinks: false,
      trackAttributes: false,

      // Session replay must be sampled. Production traffic filled the
      // OpenPanel ClickHouse disk at 100% replay capture, and analytics event
      // ingestion is more important than replay coverage. A 5% baseline gives
      // us enough product/debug coverage without letting replay dominate disk.
      //
      // Override with VITE_OPENPANEL_SESSION_REPLAY_SAMPLE_RATE when running a
      // short diagnostic window. Use 0 to disable replay entirely.
      sessionReplay: {
        enabled: sessionReplaySampleRate > 0,
        sampleRate: sessionReplaySampleRate,
      },
    });

    // Ensures SDK-owned events and raw third-party consumers carry the same
    // build dimensions as Jungle's typed wrapper events.
    realOpenPanelClient.setGlobalProperties(getClientBuildInfo());

    openPanelSingletonClient = {
      track: (eventName, properties) => {
        try {
          realOpenPanelClient.track(eventName, properties);
        } catch (trackError) {
          console.error("[openpanel-client] track failed", trackError);
        }
      },
      identify: (profileObject) => {
        try {
          realOpenPanelClient.identify(profileObject);
        } catch (identifyError) {
          console.error("[openpanel-client] identify failed", identifyError);
        }
      },
      setGlobalProperties: (properties) => {
        try {
          realOpenPanelClient.setGlobalProperties(properties);
        } catch (setGlobalError) {
          console.error("[openpanel-client] setGlobalProperties failed", setGlobalError);
        }
      },
      screenView: (pageUrl) => {
        try {
          realOpenPanelClient.screenView(pageUrl);
        } catch (screenViewError) {
          console.error("[openpanel-client] screenView failed", screenViewError);
        }
      },
      clear: () => {
        try {
          realOpenPanelClient.clear();
          // Defensive reapplication in case a future SDK release changes
          // clear() to reset globals along with profile/session identity.
          realOpenPanelClient.setGlobalProperties(getClientBuildInfo());
        } catch (clearError) {
          console.error("[openpanel-client] clear failed", clearError);
        }
      },
      isOpenPanelEnabled: true,
      // Exposed so callers that need the full SDK surface (e.g. Tranzmit
      // via window.openpanel) can reach methods we don't proxy through
      // the wrapper. Internal Jungle code should keep using the wrapper
      // methods above for the try/catch + no-op safety.
      rawOpenPanelSdkInstanceOrNull: realOpenPanelClient,
    };
  } catch (initializationError) {
    console.error("[openpanel-client] initialization failed — falling back to no-op", initializationError);
    // openPanelSingletonClient already points at the no-op
  }
}

/**
 * Get the shared OpenPanel client. Returns a no-op shim if the SDK isn't
 * initialized (dev builds, missing env var, init failure). Always safe to
 * call methods on the returned object.
 *
 * Usage:
 *   import { getOpenPanelClientForJungleWebApp } from "...openpanel-client";
 *   const op = getOpenPanelClientForJungleWebApp();
 *   op.track("event_name", { ... });
 */
export function getOpenPanelClientForJungleWebApp(): JungleOpenPanelClient {
  return openPanelSingletonClient;
}

/**
 * Internal shape of the `@openpanel/web` OpenPanel instance that we reach
 * past the public `.d.ts` to call. `loadReplayModule` is marked private in
 * the type defs but is the same method the SDK's own constructor calls to
 * dynamic-import the replay recorder; `send` is public on `OpenPanelBase`.
 *
 * We narrow what we touch to these two methods so a future SDK upgrade that
 * renames or removes them gives us a focused type-check failure rather than
 * a runtime crash.
 */
interface OpenPanelRuntimeReplayInternals {
  loadReplayModule(): Promise<{
    startReplayRecorder?: (
      sessionReplayOptions: {
        enabled: boolean;
        sampleRate: number;
        maskAllInputs?: boolean;
        maskAllText?: boolean;
        blockSelector?: string;
        blockClass?: string;
        ignoreSelector?: string;
        unmaskTextSelector?: string;
        flushIntervalMs?: number;
        maxEventsPerChunk?: number;
        maxPayloadBytes?: number;
      },
      sendReplayChunkCallback: (chunk: unknown) => void
    ) => void;
  } | null>;
  send(payload: { type: "replay"; payload: unknown }): Promise<unknown>;
}

/**
 * Force-start OpenPanel session replay at 100% fidelity for the CURRENT page
 * load. Scope is limited to this session — nothing is persisted, so closing
 * the tab and reopening it returns the user to baseline sampling.
 *
 * Called when a user starts a high-value journey such as an AI voice tutor
 * call, paywall flow, cancellation flow, or live recording.
 *
 * The mid-session bump works by reaching past the OpenPanel SDK's public
 * surface: the SDK only decides whether to record at constructor time
 * (`Math.random() < sampleRate`), with no public runtime API to flip
 * sampling. We invoke the same private `loadReplayModule` +
 * `startReplayRecorder` path the SDK uses internally. The replay module
 * self-stops any previous recorder before starting a new one, so calling
 * this when replay is already running is safe (it briefly flushes and
 * restarts — net effect is a continuous session in OpenPanel's storage).
 *
 * Once started, rrweb records every interaction in this page-load until the
 * tab closes — we intentionally do NOT stop recording on call-end, since
 * post-call activity (the user dismissing artifacts, navigating, etc.) is
 * still part of the tutor-engagement story worth seeing.
 *
 * Fire-and-forget — failure must never break the voice-call start path.
 * Always resolves (never throws).
 */
export async function forceStartOpenPanelHighFidelitySessionReplay(): Promise<void> {
  if (hasForceStartedOpenPanelHighFidelityReplayThisPageLoad) {
    // Already running at full fidelity (either we started it earlier in
    // this page-load, or the SDK auto-started it because sampleRate=1).
    return;
  }
  hasForceStartedOpenPanelHighFidelityReplayThisPageLoad = true;

  const realOpenPanelSdkInstance = openPanelSingletonClient.rawOpenPanelSdkInstanceOrNull;
  if (!realOpenPanelSdkInstance) {
    // SDK is in no-op mode (dev build, missing client id, init failed).
    // Nothing to start mid-session, but the persisted flag above means
    // the next session with the SDK alive will boot at 100%.
    return;
  }

  const openPanelReplayInternals = realOpenPanelSdkInstance as unknown as OpenPanelRuntimeReplayInternals;

  try {
    const replayModule = await openPanelReplayInternals.loadReplayModule();
    if (!replayModule?.startReplayRecorder) {
      // Replay module shape changed in a future SDK release. Reset the
      // guard so a subsequent attempt could retry; log loudly so the
      // breakage is visible in DevTools without crashing the call.
      hasForceStartedOpenPanelHighFidelityReplayThisPageLoad = false;
      console.warn(
        "[openpanel-client] OpenPanel replay module did not expose " +
          "startReplayRecorder — high-fidelity session replay for the " +
          "call-monkey cohort is disabled on this session."
      );
      return;
    }

    replayModule.startReplayRecorder(
      // Match the SDK's own defaults for masking — keep PII out of replays.
      // `maskAllInputs` and `maskAllText` default to true inside the recorder
      // when omitted, but we pass them explicitly so the contract is visible
      // at the call site.
      { enabled: true, sampleRate: 1, maskAllInputs: true, maskAllText: true },
      (replayChunk) => {
        try {
          void openPanelReplayInternals.send({
            type: "replay",
            payload: replayChunk,
          });
        } catch (sendReplayChunkError) {
          console.error("[openpanel-client] failed to send replay chunk", sendReplayChunkError);
        }
      }
    );
  } catch (forceStartReplayError) {
    // Allow a retry on the next voice-call start.
    hasForceStartedOpenPanelHighFidelityReplayThisPageLoad = false;
    console.error("[openpanel-client] forceStartOpenPanelHighFidelitySessionReplay failed", forceStartReplayError);
  }
}
